import React from 'react'

import {Colors, S2} from '../UI/index.js'
import {Input} from '../UI/forms/index.js'
import {applicableRequirements} from '../../../utils/passwordRequirements.js'
import t from 'src/helpers/Languages.js'

/** A password field with an eye to show what was typed. */
export function PasswordInput({label, value, autoComplete = 'new-password', isInvalid, onChange}: {label: string, value: string, autoComplete?: string, isInvalid?: string | boolean, onChange: (value: string) => void}) {
    return (
        <Input
            label={label}
            type='password'
            autoComplete={autoComplete}
            value={value}
            isInvalid={isInvalid}
            onChange={(event) => onChange(event.target.value)}
        />
    )
}

type State = 'empty' | 'met' | 'unmet'

const stateStyle: Record<State, {icon: string, color: string}> = {
    empty: {icon: 'mdi mdi-check-circle', color: Colors.textMedium},
    met: {icon: 'mdi mdi-check-circle', color: Colors.successBright},
    unmet: {icon: 'mdi mdi-close-circle', color: Colors.errorBright}
}

/**
 * "Je nieuwe wachtwoord moet:" with the requirements, colouring while typing: grey before anything is typed, green
 * when met, red when not. currentPassword adds "different from the current one", repeat adds "entered twice".
 */
export function PasswordRequirements({password, repeat, currentPassword}: {password: string, repeat: string, currentPassword?: string}) {
    const rows: {key: string, text: string, state: State}[] = applicableRequirements(currentPassword !== undefined).map((requirement) => ({
        key: requirement.id,
        text: t(requirement.text),
        state: !password ? 'empty' : requirement.met(password, currentPassword) ? 'met' : 'unmet'
    }))

    rows.push({key: 'repeated', text: t('passwordRequirementRepeated'), state: !repeat ? 'empty' : repeat === password ? 'met' : 'unmet'})

    return (
        <div style={{display: 'flex', flexDirection: 'column', gap: 3}}>
            <S2 style={{fontWeight: 500}}>{t('passwordRequirementsIntro')}</S2>
            {rows.map(({key, text, state}) => (
                <div key={key} style={{display: 'flex', alignItems: 'center', gap: 6}}>
                    <i className={stateStyle[state].icon} style={{color: stateStyle[state].color, fontSize: 16}} aria-hidden='true' />
                    <S2 style={{color: state === 'empty' ? Colors.textMedium : undefined}}>{text}</S2>
                </div>
            ))}
        </div>
    )
}

/** Whether the new password can be saved: every requirement met and entered the same twice. */
export function passwordReady(password: string, repeat: string, currentPassword?: string) {
    return applicableRequirements(currentPassword !== undefined).every((requirement) => requirement.met(password, currentPassword)) && password === repeat
}
